/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import { faFutbol } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import * as R from 'ramda';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { withBorder } from '../../reactutils/withBorder';
import { withNavLocation } from '../../reactutils/withNavLocation';
import { withTitle } from '../../reactutils/withTitle';
import { myProfileBettingPath } from '../../screens/UserProfileScreen';
import { Game } from '../../services/betting';
import { bettingProfileFavoriteTeamsSelector } from '../BettingApp/BettingProfile/selectors';
import { LiveTicker } from '../BettingApp/LiveTicker';
import { TimeStr } from '../DateTime';
import { usePathSegment } from '../SysAdminApp/CacheStatisticsComponent';
import { getGameApi2, getSystemConfig } from '../System/systemConfig';
import { TeamLink, isFavoriteTeam } from '../TeamLink';
import { GameResult } from '../TournamentApp/GameResult';
import { LoadingIndicatorContent, LoadingIndicatorSpinner } from '../common/LoadingIndicatorSpinner';
import { GamesListPager, TODAY_STR, paramDateToQueryDate } from './GamesListPager';

export const isGameLive = (date: Date) => {
    // game is live half an hour before the game is started until 2,5 hours after game start
    const live = date && moment(date).subtract(30, 'minutes').isBefore() && moment(date).add(150, 'minutes').isAfter();
    return live;
};

const GameListComponent: React.FC<{}> = () => {
    const [date] = usePathSegment(3);
    const [filter] = usePathSegment(4) as GameListFilterType[];

    const [games, setGames] = React.useState<Game[]>();
    const [filteredGames, setFilteredGames] = React.useState<Game[]>();

    const [showLiveTicker, setShowLiveTicker] = React.useState('');

    const favoriteTeams = useSelector(bettingProfileFavoriteTeamsSelector);

    const gamesQuery = useQuery({
        queryKey: ['getDailyGames', date, filter],
        queryFn: () => getGameApi2().getDailyGames({tenant: getSystemConfig().clientName, day: paramDateToQueryDate(date)}),
        refetchInterval: 60 * 1000, // every minute
    });

    React.useEffect(() => {
        setGames(gamesQuery.data);
    }, [gamesQuery.data]);

    const hasFavoriteTeam = React.useCallback(
        (g: Game): boolean => {
            return isFavoriteTeam(g.team1.id, favoriteTeams) || isFavoriteTeam(g.team2.id, favoriteTeams);
        },
        [favoriteTeams],
    );

    React.useEffect(() => {
        if (games) {
            setFilteredGames(
                games
                    .filter(
                        (g) =>
                            filter === 'all' ||
                            (filter === 'live' && g && isGameLive(g.date)) ||
                            (filter === 'my' && g && hasFavoriteTeam(g)),
                    )
                    .sort((a, b) => getGameGroupKey(a).localeCompare(getGameGroupKey(b))),
            );
        }
    }, [games, filter, hasFavoriteTeam]);

    const getGameGroupKey = (g: Game): string => {
        return moment(g.date).format('YYYY-MM-DD-HH-mm') + '-' + g.tournamentRound.id;
    };

    const onShowLiveTicker = (gameId: string) => {
        setShowLiveTicker(gameId);
    };

    function renderGames(games: Game[]) {
        let lastGame: Game | null = null;
        let count = 0;
        if (games) {
            return games.map((g) => {
                if (g === null) {
                    return null;
                }
                const r = (
                    <React.Fragment key={g.id}>
                        {(lastGame === null || getGameGroupKey(lastGame) !== getGameGroupKey(g)) && (
                            <>
                                <tr className="bg-secondary p-0">
                                    <td className="py-1 pl-0 h6">
                                        <TimeStr date={g.date} />
                                    </td>
                                    <td colSpan={3} className="py-1 pl-0 h6">
                                        {`${g.tournament.name}, ${g.tournamentRound.longName}`}
                                    </td>
                                </tr>
                            </>
                        )}
                        <tr key={g.id} className={count % 2 ? 'bg-white' : 'bg-white'}>
                            <td className="align-middle py-1">
                                <LoadingIndicatorContent isLoading={gamesQuery.isFetching}>
                                    <TeamLink teamId={g.team1.id} withIcon/>
                                </LoadingIndicatorContent>
                            </td>
                            <td className="align-middle py-1">
                                <LoadingIndicatorContent isLoading={gamesQuery.isFetching}>
                                    <TeamLink teamId={g.team2.id} withIcon/>
                                </LoadingIndicatorContent>
                            </td>
                            <td className="align-middle py-1 text-center">
                                <GameResult gameId={String(g.id)} date={g.date} />
                            </td>
                            <td className="align-middle py-1 px-0 text-center">
                                {g.hasEvents && (
                                    <>
                                        <Button
                                            color="link"
                                            className="m-0 p-0"
                                            onClick={() => onShowLiveTicker(String(g.id))}
                                        >
                                            <FontAwesomeIcon icon={faFutbol} title="Live-Ticker" />
                                        </Button>
                                        <Modal
                                            isOpen={showLiveTicker === String(g.id)}
                                            toggle={() => setShowLiveTicker('')}
                                            size='lg'
                                        >
                                            <ModalHeader toggle={() => setShowLiveTicker('')}>Live-Ticker</ModalHeader>
                                            <ModalBody>
                                                <LiveTicker gameId={String(g.id)} />
                                            </ModalBody>
                                            <ModalFooter>
                                                <Button color="secondary" onClick={() => setShowLiveTicker('')}>
                                                    Schließen
                                                </Button>
                                            </ModalFooter>
                                        </Modal>
                                    </>
                                )}
                            </td>
                        </tr>
                    </React.Fragment>
                );
                lastGame = g;
                count++;
                return r;
            });
        }
    }
    if (!filteredGames) {
        return (
            <>
                loading...
                <LoadingIndicatorSpinner />
            </>
        );
    }

    return (
        <>
            <GamesListPager date={date} filter={filter} />
            {filteredGames.length > 0 && (
                <table className="table">
                    <thead>
                        <tr>
                            <th colSpan={2}>Ansetzung</th>
                            <th className="text-center">Ergebnis</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{renderGames(filteredGames)}</tbody>
                </table>
            )}
            {filteredGames.length === 0 && filter === 'live' && (
                <Alert color="primary" className="mt-3">
                    <h4>Live-Ergebnisse</h4>
                    <p>Aktuell findet kein Spiel statt.</p>
                    <Link className="ms-0" to={gamesListPath(date, 'all')}>
                        Alle Spiele anzeigen.
                    </Link>
                </Alert>
            )}
            {filteredGames.length === 0 && filter === 'my' && (
                <Alert color="primary" className="mt-3">
                    <h4>Ergebnisse meiner Lieblingsmannschaften</h4>
                    <p>
                        Aktuell findet kein Spiel Deiner Mannschaften statt oder Du hast keine
                        <Link to={myProfileBettingPath()}> Lieblingsmannschaften </Link>
                        ausgewählt.
                    </p>
                    <Link className="ms-0" to={gamesListPath(date, 'all')}>
                        Alle Spiele anzeigen.
                    </Link>
                </Alert>
            )}
            {filteredGames.length === 0 && filter === 'all' && (
                <Alert color="primary" className="mt-3">
                    <h4>Spiele</h4>
                    <p>An diesem Tag sind keine Spiele verfügbar.</p>
                </Alert>
            )}
            {filteredGames.length > 0 && <GamesListPager date={date} filter={filter} />}
        </>
    );
};

export type GameListFilterType = 'all' | 'live' | 'my';

export const gamesListPath = (date: string, filter: GameListFilterType) => {
    const link = '/b/games/' + date + '/' + filter;
    return link;
};

export const gamesListTodayPath = () => gamesListPath(TODAY_STR, 'all');

export interface LoadGamesListParams {
    day: string;
    filter: GameListFilterType;
}

const GameListSubTitle: React.FC<{}> = () => {
    const [day] = usePathSegment(3);
    const [filter] = usePathSegment(4) as GameListFilterType[];

    const [queryDay, setQueryDay] = React.useState('');
    const [subTitleText, setSubTitleText] = React.useState('');
    const [isToday, setIsToday] = React.useState(false);

    const updateSubTitle = React.useCallback(() => {
        setQueryDay(paramDateToQueryDate(day));
        setSubTitleText(moment(queryDay).locale('de').format('dddd, DD. MMM YYYY'));
        setIsToday(moment().format('YYYYMMDD') === queryDay);
    }, [day, queryDay]);

    React.useEffect(() => {
        updateSubTitle();
        const interval = setInterval(() => updateSubTitle(), 60 * 1000);
        return () => clearInterval(interval);
    }, [updateSubTitle]);

    return (
        <>
            <div className="d-flex align-items-baseline justify-content-between flex-wrap m-0">
                <Helmet>
                    <title>Spieltag: {subTitleText}</title>
                </Helmet>
                {subTitleText && <div className="m-0 p-0">{subTitleText}</div>}
                <>
                    <div className="ms-1 my-1 d-flex flex-nowrap" style={{ fontSize: '18px' }}>
                        {filter === 'live' && isToday && (
                            <>
                                <span className="mx-1 text-warning fw-bold">Live</span>/
                            </>
                        )}
                        {filter !== 'live' && isToday && (
                            <>
                                <Link color="link" className="mx-1" to={gamesListPath(day, 'live')} title="live">
                                    Live
                                </Link>
                                /
                            </>
                        )}
                        {filter === 'all' && <span className="mx-1 text-warning fw-bold">alle Spiele</span>}
                        {filter !== 'all' && (
                            <Link color="link" className="mx-1" to={gamesListPath(day, 'all')} title="alle Spiele">
                                alle Spiele
                            </Link>
                        )}
                        /{filter === 'my' && <span className="mx-1 text-warning fw-bold">Lieblingsteams</span>}
                        {filter !== 'my' && (
                            <Link color="link" className="mx-1" to={gamesListPath(day, 'my')} title="Lieblingsteams">
                                Lieblingsteams
                            </Link>
                        )}
                    </div>
                </>
            </div>
        </>
    );
};

export const GameList = R.compose(
    withBorder(),
    withTitle('', () => <GameListSubTitle />),
    withNavLocation('live'),
)(GameListComponent);
